import { Client } from '../../services/crmService';

export type ContactStage = Client['stage'];
/** The three list routes are backed by these lifecycle stages. */
export type ListStage = 'lead' | 'prospect' | 'client';

export interface StageMeta {
  /** Plural label used for the route/list title. */
  pluralLabel: string;
  /** Singular label used in the detail header/badge. */
  singularLabel: string;
  badgeClass: string;
}

export const STAGE_META: Record<ContactStage, StageMeta> = {
  lead: { pluralLabel: 'Leads', singularLabel: 'Lead', badgeClass: 'bg-primary/10 text-primary border-primary/30' },
  prospect: { pluralLabel: 'Prospects', singularLabel: 'Prospect', badgeClass: 'bg-warning-fg/10 text-warning-fg border-warning-fg/30' },
  client: { pluralLabel: 'Customers', singularLabel: 'Customer', badgeClass: 'bg-success-fg/10 text-success-fg border-success-fg/30' },
  churned: { pluralLabel: 'Churned', singularLabel: 'Churned', badgeClass: 'bg-fg-muted/10 text-fg-muted border-border' },
  lost: { pluralLabel: 'Lost', singularLabel: 'Lost', badgeClass: 'bg-danger-fg/10 text-danger-fg border-danger-fg/30' },
  spam: { pluralLabel: 'Spam', singularLabel: 'Spam', badgeClass: 'bg-fg-muted/10 text-fg-muted border-border' },
};

export interface ConversionAction {
  id: string;
  label: string;
  /** Target stage the transition service will move the contact to. */
  toStage: ContactStage;
  /** primary => filled button; secondary => outline; danger => destructive. */
  intent: 'primary' | 'secondary' | 'danger';
  /** When true, the UI must collect a lost_reason before submitting. */
  requiresLostReason?: boolean;
}

/**
 * Stage-dependent primary actions. The UI never writes Client.stage directly —
 * each action PATCHes {stage,...} which the backend routes through the
 * stage-transition service.
 */
export const CONVERSION_ACTIONS: Record<ContactStage, ConversionAction[]> = {
  lead: [
    { id: 'to-prospect', label: 'Convert to Prospect', toStage: 'prospect', intent: 'primary' },
    { id: 'mark-lost', label: 'Mark Lost', toStage: 'lost', intent: 'danger', requiresLostReason: true },
    { id: 'mark-spam', label: 'Mark Spam', toStage: 'spam', intent: 'secondary' },
  ],
  prospect: [
    { id: 'to-customer', label: 'Convert to Customer', toStage: 'client', intent: 'primary' },
    { id: 'mark-lost', label: 'Mark Lost', toStage: 'lost', intent: 'danger', requiresLostReason: true },
  ],
  client: [],
  churned: [
    { id: 'reopen', label: 'Reopen as Lead', toStage: 'lead', intent: 'secondary' },
  ],
  lost: [
    { id: 'reopen', label: 'Reopen as Lead', toStage: 'lead', intent: 'secondary' },
  ],
  spam: [
    { id: 'reopen', label: 'Reopen as Lead', toStage: 'lead', intent: 'secondary' },
  ],
};

export const LOST_REASONS: Array<{ value: string; label: string }> = [
  { value: 'price', label: 'Price' },
  { value: 'no_response', label: 'No response' },
  { value: 'chose_competitor', label: 'Chose competitor' },
  { value: 'not_qualified', label: 'Not qualified' },
  { value: 'timing', label: 'Timing' },
  { value: 'other', label: 'Other' },
];

/** Short one-line qualification summary shown in list rows. */
export function qualificationSummary(c: Client): string {
  const parts = [c.service_requested, c.service_location, c.property_type].filter(Boolean);
  return parts.join(' · ');
}
